// src/components/checklists/JobChecklistsPanel.js
// chklstweb-3a7f1c58 — CHECKLISTS SEAM 3: the Checklists panel in the web job pop-up.
//
// Sits between Work Performed and Notes & Media, on Jobs only. Data arrives live from
// useJobChecklists (called once by JobQuickViewModal and passed down), so the panel
// and the "can't complete yet" dialog read the same snapshot.
//
// WHO SEES WHAT (display only — every write is a Seam 1 callable that re-checks):
//   fill / add      Office Staff and above, or a Field Technician on this job
//   remove (×)      office roles any checklist; a technician only one they added by hand —
//                   EXCEPT one that blocks completion and isn't finished: Owner/Admin/Manager, with a
//                   reason, recorded and shown on the job (clremove-3f7d1a85)
//   unlock / lock   office roles
//   removed list    office roles: every removed checklist (who / when / why) + a READ-ONLY View — no
//                   restore, no add-back from it (chk7d-4c7e1a92)
//   print / PDF     office roles, every active checklist, even one that is office only (custcopy-32471cbd);
//                   the PDF is the customer copy: "Office only" questions and Instructions left out
//   send            office roles, when the template's "Customers can receive a copy" is on (Seam 8c); each
//                   send shows "Sent to … by … · date" + "View what was sent" (office roles; the exact PDF)
//   Sales Consultant and anyone else: read-only.
// Adding and removing stop once the job is done (even when unlocked — the server
// refuses both); answers are editable while not done or while unlocked.
//
// Staff-only: nothing here reaches the portal or invoices. The checklist collections have
// no portal clause, and checklist photo notes are forced internal by the rules. The ONLY
// way a checklist reaches a customer is the office's deliberate Send (custcopy-32471cbd),
// which emails the customer copy (office-only questions left out) through the server.

import React, { useEffect, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { ClipboardCheck, Plus, X, Lock, Unlock, ShieldAlert, ChevronUp, Loader2, Archive, Printer, Send } from 'lucide-react';
import { db } from '../../firebase/config';
import {
    attachChecklist, removeChecklist, setChecklistsLocked,
    getChecklistCopyPdf, getChecklistSentPdf, downloadPdfBytes, // custcopy-32471cbd
    canFillChecklists, canLockChecklists, isChecklistOfficeRole, canOverrideChecklist,
    isChecklistJobDone, isChecklistEditable,
    isChecklistBlockingUnfinished, OVERRIDE_REASON_MAX, // clremove-3f7d1a85
    removedChecklistRows, // chk7d-4c7e1a92
} from '../../utils/checklistClient';
import ChecklistFiller from './ChecklistFiller';
import useRemovedJobChecklists from '../../hooks/useRemovedJobChecklists'; // chk7d-4c7e1a92
import useChecklistSends from '../../hooks/useChecklistSends'; // custcopy-32471cbd
import ChecklistSendDialog from './ChecklistSendDialog'; // custcopy-32471cbd

const tsToDate = (v) => (v?.toDate ? v.toDate() : (v instanceof Date ? v : null));

const JobChecklistsPanel = ({ job, orgId, currentUserProfile, currentUserId, checklists, state, focusRequest }) => {
    const [openId, setOpenId] = useState(null);
    const [highlightIds, setHighlightIds] = useState(() => new Set());
    const [menuOpen, setMenuOpen] = useState(false);
    const [templates, setTemplates] = useState(null); // null = not loaded
    const [templatesError, setTemplatesError] = useState('');
    // Immediate feedback while a callable is out (a cold start can take seconds).
    const [pendingAdd, setPendingAdd] = useState(null);           // { templateId, name, blocksCompletion }
    const [removingIds, setRemovingIds] = useState(() => new Set());
    const [lockAction, setLockAction] = useState(null);           // 'locking' | 'unlocking' | null
    const [removeAsk, setRemoveAsk] = useState(null);             // { id, reason } — clremove-3f7d1a85
    const [viewRemovedId, setViewRemovedId] = useState(null);     // the removed checklist open read-only — chk7d-4c7e1a92
    const [printingId, setPrintingId] = useState(null);           // custcopy-32471cbd — the checklist whose PDF is being made
    const [sendFor, setSendFor] = useState(null);                 // custcopy-32471cbd — the checklist in the Send dialog
    const [viewingSendId, setViewingSendId] = useState(null);     // the saved copy being fetched ("View what was sent")
    const rowRefs = useRef({});

    const uid = currentUserId || currentUserProfile?.id;
    const done = isChecklistJobDone(job);
    const canFill = canFillChecklists(currentUserProfile, job, uid);
    const canAddRemove = canFill && !done;
    const answersEditable = canFill && isChecklistEditable(job, state);
    const isOffice = isChecklistOfficeRole(currentUserProfile);
    const locked = done && !(state && state.checklistsUnlocked === true);
    // chk7d-4c7e1a92 — removed checklists stay on record; office roles can find and read them here.
    const removedRows = removedChecklistRows(useRemovedJobChecklists({ orgId, jobId: job.id, enabled: isOffice }), state);
    const removedIds = new Set(removedRows.map(r => r.id));
    // custcopy-32471cbd — Seam 8c: what was emailed from this job (office roles only; the rules allow no one else).
    const sendsByChecklist = useChecklistSends({ orgId, jobId: job.id, enabled: isOffice });
    const openRemoved = (id) => {
        setViewRemovedId(id);
        setTimeout(() => rowRefs.current[`removed-${id}`]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    };

    // "Finish it" from the gate dialog: open that checklist, outline what's missing.
    useEffect(() => {
        if (!focusRequest?.checklistId) return;
        const id = focusRequest.checklistId;
        setOpenId(id);
        setHighlightIds(prev => new Set(prev).add(id));
        setTimeout(() => rowRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }, [focusRequest]);

    const openMenu = async () => {
        setMenuOpen(v => !v);
        if (templates !== null) return;
        setTemplatesError('');
        try {
            const snap = await getDocs(collection(db, `organizations/${orgId}/checklistTemplates`));
            setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            setTemplatesError('Could not load checklists. Try again.');
            setTemplates(null);
        }
    };

    // custcopy-32471cbd — the office needs each template's "Customers can receive a copy" switch to show
    // Send, so the template list loads up front for office roles (it otherwise loads with the Add menu).
    useEffect(() => {
        if (!isOffice || !orgId || templates !== null) return undefined;
        let live = true;
        getDocs(collection(db, `organizations/${orgId}/checklistTemplates`))
            .then((snap) => { if (live) setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() }))); })
            .catch(() => { /* Send stays hidden; the Add menu retries on open */ });
        return () => { live = false; };
    }, [isOffice, orgId, templates]);
    const customerCopyOn = (c) => (templates || []).some(t => t.id === c.templateId && t.customerCopy === true);

    const handleViewSent = async (sendId) => {
        if (viewingSendId) return;
        setViewingSendId(sendId);
        try {
            const res = await getChecklistSentPdf({ sendId });
            downloadPdfBytes(res.pdfBase64, res.fileName);
        } catch (err) {
            window.alert(err?.message || 'Could not open the saved copy. Please try again.');
        } finally {
            setViewingSendId(null);
        }
    };

    const activeTemplateIds = new Set(checklists.map(c => c.templateId));
    const addable = (templates || [])
        .filter(t => t.archived !== true && !activeTemplateIds.has(t.id))
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

    // The "Adding…" row stays until the REAL row arrives through the listener, so it
    // never blinks out between the callable answering and the snapshot landing.
    useEffect(() => {
        if (pendingAdd && checklists.some(c => c.templateId === pendingAdd.templateId)) setPendingAdd(null);
        setRemovingIds(prev => {
            if (!prev.size) return prev;
            const next = new Set([...prev].filter(id => checklists.some(c => c.id === id)));
            return next.size === prev.size ? prev : next;
        });
    }, [checklists, pendingAdd]);

    const handleAttach = async (template) => {
        if (pendingAdd) return; // one add at a time — no double-add
        setMenuOpen(false);
        setPendingAdd({ templateId: template.id, name: template.name, blocksCompletion: template.blocksCompletion === true });
        try {
            const res = await attachChecklist({ jobId: job.id, templateId: template.id });
            if (res.checklistId) setOpenId(res.checklistId);
        } catch (err) {
            setPendingAdd(null);
            window.alert(err?.message || 'That checklist could not be added. Try again.');
        }
    };

    const handleRemove = async (c, reason = null) => {
        // clremove-3f7d1a85 — a blocking, unfinished checklist asks for a reason instead of a plain confirm.
        if (reason === null && isChecklistBlockingUnfinished(c)) { setRemoveAsk({ id: c.id, reason: '' }); return; }
        if (reason === null && !window.confirm('Remove this checklist? Its answers are kept — adding it again brings them back.')) return;
        setRemoveAsk(null);
        setRemovingIds(prev => new Set(prev).add(c.id));
        try {
            await removeChecklist({ jobId: job.id, checklistId: c.id, ...(reason !== null ? { reason } : {}) });
            if (openId === c.id) setOpenId(null);
            // The row (and its "Removing…" state) goes when the listener drops it.
        } catch (err) {
            setRemovingIds(prev => { const next = new Set(prev); next.delete(c.id); return next; });
            window.alert(err?.message || 'That checklist could not be removed. Try again.');
        }
    };

    // custcopy-32471cbd — Print / PDF: the customer copy (office-only questions left out) arrives
    // AS BYTES in the server's reply (nothing is stored, no link exists) and is saved as a file,
    // like the invoice PDF: the browser downloads it and opens it in the PDF viewer.
    const handlePrint = async (c) => {
        if (printingId) return;
        setPrintingId(c.id);
        try {
            const res = await getChecklistCopyPdf({ jobId: job.id, checklistId: c.id });
            downloadPdfBytes(res.pdfBase64, res.fileName); // the file lives only in this browser
        } catch (err) {
            window.alert(err?.message || 'Could not make the PDF. Please try again.');
        } finally {
            setPrintingId(null);
        }
    };

    const handleLock = async (lockIt) => {
        setLockAction(lockIt ? 'locking' : 'unlocking');
        try {
            await setChecklistsLocked({ jobId: job.id, locked: lockIt });
        } catch (err) {
            window.alert(err?.message || 'That could not be changed. Try again.');
        } finally {
            setLockAction(null);
        }
    };

    const canRemove = (c) => canAddRemove && (isChecklistBlockingUnfinished(c)
        ? canOverrideChecklist(currentUserProfile) // clremove-3f7d1a85 — Owner / Admin / Manager, with a reason
        : (isOffice || (c.addedBy?.uid === uid && c.addedBy?.auto !== true)));

    // clremove-3f7d1a85 — blocking checklists removed before they were finished, shown while still removed.
    const activeIds = new Set(checklists.map(c => c.id));
    const removals = Object.entries(state?.checklistRemovals || {})
        .filter(([id, r]) => r && !activeIds.has(id))
        .map(([id, r]) => ({ id, ...r, atDate: tsToDate(r.at) }));

    const completeCount = checklists.filter(c => c.status === 'complete').length;
    const override = state?.checklistOverride || null;
    const overrideAt = tsToDate(override?.at);
    const doneWord = job.status === 'Completed' ? 'complete' : 'canceled';

    // custcopy-32471cbd — "Sent to <email> by <name> · <date>" under a checklist, newest first (3 shown),
    // with "View what was sent" (the exact PDF that went out, office roles). A send that failed says so.
    const SENDS_SHOWN = 3;
    const renderSends = (c) => {
        const sends = (sendsByChecklist[c.id] || []).filter(s => s.status === 'sent' || s.status === 'failed');
        if (!sends.length) return null;
        const when = (s) => {
            const d = tsToDate(s.at);
            const withInvoice = s.via === 'invoice' && s.invoiceNumber ? ` (with invoice #${s.invoiceNumber})` : ''; // custcopy-32471cbd 8d
            return `${d ? ` · ${format(d, 'MMM d, yyyy h:mm a')}` : ''}${withInvoice}`;
        };
        return (
            <ul className="mt-2 space-y-0.5">
                {sends.slice(0, SENDS_SHOWN).map(s => (s.status === 'sent' ? (
                    <li key={s.id} className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1 min-w-0">
                            <Send size={10} className="text-emerald-600 flex-shrink-0" />
                            <span className="truncate">Sent to {s.to} by {s.sentBy?.name || 'someone'}{when(s)}{s.unfinished ? ' (unfinished)' : ''}</span>
                        </span>
                        <button type="button" onClick={() => handleViewSent(s.id)} disabled={!!viewingSendId}
                            className="text-[#5F4EC9] hover:underline disabled:opacity-50">
                            {viewingSendId === s.id ? 'Opening…' : 'View what was sent'}
                        </button>
                    </li>
                ) : (
                    <li key={s.id} className="text-[11px] text-amber-700">
                        Not sent to {s.to}{when(s)}{s.failReason ? ` — ${s.failReason}` : ''}
                    </li>
                )))}
                {sends.length > SENDS_SHOWN && (
                    <li className="text-[11px] text-slate-400">and {sends.length - SENDS_SHOWN} earlier</li>
                )}
            </ul>
        );
    };

    const renderRow = (c) => {
        const total = Number(c.requiredTotal) || 0;
        const answered = Number(c.requiredAnswered) || 0;
        const pct = c.status === 'complete' ? 100 : (total > 0 ? Math.round((answered / total) * 100) : 0);
        const progressText = c.status === 'complete' ? 'Complete'
            : c.status === 'not_started' ? 'Not started'
            : total > 0 ? `${answered} of ${total} required answered` : 'In progress';
        const removing = removingIds.has(c.id);
        const isOpen = openId === c.id && !removing;
        const buttonLabel = !answersEditable || c.status === 'complete' ? 'View'
            : c.status === 'not_started' ? 'Fill in' : 'Continue';

        if (removing) {
            return (
                <div key={c.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-2 opacity-70">
                    <p className="text-sm font-semibold text-slate-600 truncate">{c.name}</p>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500 flex-shrink-0">
                        <Loader2 size={12} className="animate-spin" /> Removing…
                    </span>
                </div>
            );
        }

        return (
            <div key={c.id} ref={el => { rowRefs.current[c.id] = el; }} className="rounded-lg border border-slate-200 bg-white">
                <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                            {c.blocksCompletion && (
                                <span className="inline-block mt-0.5 text-[10px] font-semibold text-[#5F4EC9] bg-[#5F4EC9]/10 px-1.5 py-0.5 rounded-full">
                                    Required to complete
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {isOffice && ( // custcopy-32471cbd — office roles; works even when the checklist is office only
                                <button type="button" onClick={() => handlePrint(c)} disabled={!!printingId}
                                    title="Open the customer copy as a PDF (questions marked Office only are left out)"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50">
                                    {printingId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
                                    {printingId === c.id ? 'Making PDF…' : 'Print / PDF'}
                                </button>
                            )}
                            {isOffice && customerCopyOn(c) && ( // custcopy-32471cbd — hidden when the checklist is office only
                                <button type="button" onClick={() => setSendFor(c)}
                                    title="Email the customer a copy (questions marked Office only are left out)"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200">
                                    <Send size={12} /> Send
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setOpenId(isOpen ? null : c.id)}
                                className={`px-3 py-1 text-xs font-semibold rounded-lg ${isOpen
                                    ? 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                                    : 'text-white bg-[#5F4EC9] hover:bg-[#4f3fb5]'}`}
                            >
                                {isOpen ? <span className="flex items-center gap-1"><ChevronUp size={12} /> Close</span> : buttonLabel}
                            </button>
                            {canRemove(c) && (
                                <button type="button" onClick={() => handleRemove(c)}
                                    title="Remove this checklist" aria-label={`Remove ${c.name}`}
                                    className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                    {removeAsk?.id === c.id && ( // clremove-3f7d1a85
                        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2">
                            <p className="text-xs text-amber-900">This checklist must be finished before the job can be completed, and it isn't. Say why you're removing it. Your name, the time and this reason are kept on the job.</p>
                            <textarea value={removeAsk.reason} maxLength={OVERRIDE_REASON_MAX} rows={2} autoFocus
                                onChange={e => setRemoveAsk({ id: c.id, reason: e.target.value })}
                                placeholder="Reason"
                                className="w-full text-sm border border-amber-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30" />
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-amber-700">{removeAsk.reason.length} / {OVERRIDE_REASON_MAX}</span>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setRemoveAsk(null)}
                                        className="px-3 py-1 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                                    <button type="button" disabled={!removeAsk.reason.trim()} onClick={() => handleRemove(c, removeAsk.reason.trim())}
                                        className="px-3 py-1 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-40">Remove checklist</button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full ${c.status === 'complete' ? 'bg-emerald-500' : 'bg-[#5F4EC9]'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className={`text-[11px] font-medium ${c.status === 'complete' ? 'text-emerald-600' : 'text-slate-500'}`}>{progressText}</span>
                    </div>
                    {renderSends(c)}
                </div>
                {isOpen && (
                    <div className="border-t border-slate-100 p-3">
                        <ChecklistFiller
                            checklist={c}
                            jobId={job.id}
                            orgId={orgId}
                            currentUserId={uid}
                            editable={answersEditable}
                            highlightMissing={highlightIds.has(c.id)}
                            isOffice={isOffice}
                            canRestore={canOverrideChecklist(currentUserProfile)}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="rounded-lg border border-slate-100 overflow-hidden">
            <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                    <ClipboardCheck size={11} /> Checklists
                    {checklists.length > 0 && (
                        <span className="normal-case font-normal text-slate-400">— {completeCount} of {checklists.length} complete</span>
                    )}
                </p>
                {canAddRemove && !menuOpen && (
                    <button type="button" onClick={openMenu} disabled={!!pendingAdd}
                        className="flex items-center gap-1 text-xs font-semibold text-[#5F4EC9] hover:text-[#4f3fb5] disabled:opacity-40">
                        <Plus size={12} /> Add checklist
                    </button>
                )}
            </div>

            <div className="p-3 space-y-2">
                {/* The picker sits INLINE and pushes the rows down. It must never float:
                    this panel and the pop-up's scroll area both clip anything that does. */}
                {canAddRemove && menuOpen && (
                    <div className="rounded-lg border border-[#5F4EC9]/30 bg-white">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                            <p className="text-xs font-semibold text-slate-600">Add a checklist</p>
                            <button type="button" onClick={() => setMenuOpen(false)}
                                className="text-xs font-semibold text-slate-500 hover:text-slate-800">
                                Cancel
                            </button>
                        </div>
                        <div className="py-1">
                            {templates === null && !templatesError && <p className="px-3 py-2 text-xs text-slate-400">Loading…</p>}
                            {templatesError && <p className="px-3 py-2 text-xs text-red-600">{templatesError}</p>}
                            {templates !== null && addable.length === 0 && (
                                <p className="px-3 py-2 text-xs text-slate-400">No other checklists to add.</p>
                            )}
                            {addable.map(t => (
                                <button key={t.id} type="button" onClick={() => handleAttach(t)} disabled={!!pendingAdd}
                                    className="w-full text-left px-3 py-2 hover:bg-slate-50 disabled:opacity-40">
                                    <span className="block text-sm text-slate-700">{t.name}</span>
                                    {t.blocksCompletion && (
                                        <span className="text-[10px] font-semibold text-[#5F4EC9]">Required to complete</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {override && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <ShieldAlert size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-900">
                            Completed without finishing the checklist — overridden by <span className="font-semibold">{override.name || 'someone'}</span>
                            {overrideAt ? ` on ${format(overrideAt, 'MMM d, yyyy h:mm a')}` : ''}.
                            {override.reason ? <> Reason: “{override.reason}”</> : null}
                        </p>
                    </div>
                )}

                {removals.map(r => ( // clremove-3f7d1a85
                    <div key={`rm-${r.id}`} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <ShieldAlert size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-900">
                            “{r.checklistName || 'A checklist'}” was removed before it was finished ({r.requiredAnswered} of {r.requiredTotal} required answered) — by <span className="font-semibold">{r.by?.name || 'someone'}</span>
                            {r.atDate ? ` on ${format(r.atDate, 'MMM d, yyyy h:mm a')}` : ''}.
                            {r.reason ? <> Reason: “{r.reason}”</> : null}
                            {isOffice && removedIds.has(r.id) && ( // chk7d-4c7e1a92
                                <>{' '}<button type="button" onClick={() => openRemoved(r.id)}
                                    className="font-semibold text-[#5F4EC9] hover:underline">View removed checklist</button></>
                            )}
                        </p>
                    </div>
                ))}
                {done && checklists.length > 0 && (
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="text-xs text-slate-600 flex items-center gap-1.5">
                            {locked
                                ? <><Lock size={12} /> Locked because the job is {doneWord}</>
                                : <><Unlock size={12} /> Unlocked for editing</>}
                        </p>
                        {canLockChecklists(currentUserProfile) && state && (
                            <button type="button" onClick={() => handleLock(!locked)} disabled={!!lockAction}
                                className="flex items-center gap-1 text-xs font-semibold text-[#5F4EC9] hover:underline disabled:opacity-60 disabled:no-underline">
                                {lockAction && <Loader2 size={11} className="animate-spin" />}
                                {lockAction === 'unlocking' ? 'Unlocking…'
                                    : lockAction === 'locking' ? 'Locking…'
                                    : locked ? 'Unlock to edit' : 'Lock again'}
                            </button>
                        )}
                    </div>
                )}

                {checklists.length === 0 && !pendingAdd
                    ? <p className="text-xs text-slate-400 italic">No checklists on this job.</p>
                    : checklists.map(renderRow)}

                {/* The picked checklist, shown the instant it is picked; replaced by the
                    real row when the listener delivers it. */}
                {pendingAdd && (
                    <div className="rounded-lg border border-dashed border-[#5F4EC9]/40 bg-white p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-700 truncate">{pendingAdd.name}</p>
                            {pendingAdd.blocksCompletion && (
                                <span className="inline-block mt-0.5 text-[10px] font-semibold text-[#5F4EC9] bg-[#5F4EC9]/10 px-1.5 py-0.5 rounded-full">
                                    Required to complete
                                </span>
                            )}
                        </div>
                        <span className="flex items-center gap-1.5 text-xs text-slate-500 flex-shrink-0">
                            <Loader2 size={12} className="animate-spin text-[#5F4EC9]" /> Adding…
                        </span>
                    </div>
                )}

                {isOffice && removedRows.length > 0 && ( // chk7d-4c7e1a92 — kept on record, read-only
                    <div className="pt-2 border-t border-slate-100 space-y-2">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                            <Archive size={11} /> Removed checklists ({removedRows.length})
                        </p>
                        {removedRows.map(r => {
                            const isOpen = viewRemovedId === r.id;
                            const at = tsToDate(r.at);
                            return (
                                <div key={`removed-${r.id}`} ref={el => { rowRefs.current[`removed-${r.id}`] = el; }}
                                    className="rounded-lg border border-dashed border-slate-300 bg-slate-50">
                                    <div className="p-3 flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-600">{r.name}</p>
                                            <p className="text-[11px] text-slate-500">
                                                Removed by {r.byName || 'someone'}{at ? ` · ${format(at, 'MMM d, yyyy h:mm a')}` : ''}
                                                {r.required ? ' · was required to complete' : ''}
                                            </p>
                                            {r.reason && <p className="text-[11px] text-slate-500">Reason: “{r.reason}”</p>}
                                        </div>
                                        <button type="button" onClick={() => (isOpen ? setViewRemovedId(null) : openRemoved(r.id))}
                                            className="px-3 py-1 text-xs font-semibold rounded-lg text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 flex-shrink-0">
                                            {isOpen ? <span className="flex items-center gap-1"><ChevronUp size={12} /> Close</span> : 'View removed checklist'}
                                        </button>
                                    </div>
                                    {isOpen && (
                                        <div className="border-t border-slate-200 p-3 bg-white">
                                            <p className="text-[11px] text-slate-500 mb-2">Read-only — exactly as it was when it was removed.</p>
                                            <ChecklistFiller
                                                checklist={r.copy}
                                                jobId={job.id}
                                                orgId={orgId}
                                                currentUserId={uid}
                                                editable={false}
                                                highlightMissing={false}
                                                isOffice={isOffice}
                                                canRestore={false}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <ChecklistSendDialog // custcopy-32471cbd
                isOpen={!!sendFor}
                onClose={() => setSendFor(null)}
                orgId={orgId}
                job={job}
                checklist={sendFor}
                currentUserProfile={currentUserProfile} // clfix-56ea256b — Office Staff pick an email on file
            />
        </div>
    );
};

export default JobChecklistsPanel;
